import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '../../api/client'
import { discoveryTabTarget } from './discoveryDoors'
import {
  applyDiscoveryAssistant,
  discoveryAssistantQuery,
  setDiscoveryAuto,
  type AssistantOffice,
  type AssistantResult,
  type DiscoveryAssistantPage,
} from '../../api/settingsSoftsee'
import { Modal } from '../../ui/Modal'
import { newAlert } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './DiscoveryAssistant.css'

/*
 * "SET UP DISCOVERY" - Settings > Softsee > Discovery setup, butonul de sus.
 *
 * Propunerea vine gata de la `DiscoveryAssistant` (v4/api), sediu cu sediu:
 * releul, razele, programul de noapte, optiunea SNMP 'public' si sugestiile
 * de credentiale. Aici doar se bifeaza: implicit e bifat ce API-ul a bifat
 * (releu server, raze curate, program), nebifat ce e "to check", SNMP si o
 * statie fixa propusa ca releu. "Apply N changes" trimite NUMAI cheile bifate;
 * serverul reface propunerea si aplica doar ce mai e propus.
 */

/** Cheile bifate implicit, din propunere. */
export function defaultKeys(offices: AssistantOffice[]): string[] {
  const keys: string[] = []
  for (const office of offices) {
    if (office.relay.key && office.relay.checked) keys.push(office.relay.key)
    for (const range of office.ranges) if (range.status !== 'covered' && range.checked) keys.push(range.key)
    if (office.schedule && office.schedule.action === 'set' && office.schedule.checked) keys.push(office.schedule.key)
    if (office.snmp.checked) keys.push(office.snmp.key)
  }
  return keys
}

const KIND_CLASS: Record<string, string> = {
  dc: 'label-success',
  server: 'label-success',
  desktop: 'label-warning',
  laptop: 'label-danger',
  mobile: 'label-danger',
}

function Machine({ m }: { m: NonNullable<AssistantOffice['relay']['device']> }) {
  return (
    <>
      <strong>{m.name}</strong> <span className={'label ' + (KIND_CLASS[m.kind] ?? 'label-default')}>{m.kindLabel}</span>
      <span className="text-muted small">
        {' '}
        {m.os}
        {m.ip ? ' · ' + m.ip : ''}
        {m.lastSeen ? ' · last seen ' + m.lastSeen : ''}
      </span>
    </>
  )
}

function OfficeBlock({
  office,
  picked,
  toggle,
  canApply,
  canCredentials,
  openCredentials,
}: {
  office: AssistantOffice
  picked: Set<string>
  toggle(key: string): void
  canApply: boolean
  canCredentials: boolean
  openCredentials(): void
}) {
  const relay = office.relay
  const box = (key: string, disabled = false) => (
    <input
      type="checkbox"
      data-key={key}
      aria-label={key}
      checked={picked.has(key)}
      disabled={!canApply || disabled}
      onChange={() => toggle(key)}
    />
  )
  return (
    <div className="panel panel-default" data-testid={'assistant-office-' + office.office.id}>
      <div className="panel-heading">
        <strong>{office.office.name}</strong>
        <span className="text-muted small"> · {office.agents} agent(s) reporting</span>
      </div>
      <table className="table table-condensed" style={{ marginBottom: 0 }}>
        <tbody>
          <tr data-row="relay">
            <td style={{ width: 30 }}>{relay.key ? box(relay.key) : null}</td>
            <td style={{ width: 110 }} className="text-muted">
              Relay
            </td>
            <td>
              {relay.action === 'keep' && relay.current ? (
                <>
                  <span className="label label-default">Keep</span> <Machine m={relay.current} />
                </>
              ) : null}
              {(relay.action === 'new' || relay.action === 'replace') && relay.device ? (
                <>
                  <span className="label label-primary">{relay.action === 'new' ? 'Set as relay' : 'Replace'}</span>{' '}
                  <Machine m={relay.device} />
                  {relay.replaces ? <span className="text-muted small"> · instead of {relay.replaces}</span> : null}
                </>
              ) : null}
              {relay.action === 'none' ? <span className="text-danger">No suitable relay</span> : null}
              <br />
              <span className="text-muted small">{relay.reason}</span>
              {relay.warning ? (
                <>
                  <br />
                  <span className="text-warning small">
                    <i className="fa fa-exclamation-triangle"></i> {relay.warning}
                  </span>
                </>
              ) : null}
              {relay.note ? (
                <>
                  <br />
                  <span className="text-muted small">{relay.note}</span>
                </>
              ) : null}
            </td>
          </tr>
          {office.ranges.map((range) => (
            <tr key={range.key} data-row="range" className={range.status === 'covered' ? 'text-muted' : undefined}>
              <td>{range.status === 'covered' ? null : box(range.key)}</td>
              <td className="text-muted">Range</td>
              <td>
                <strong>{range.cidr}</strong>{' '}
                {range.status === 'check' ? <span className="label label-warning">To check</span> : null}
                {range.status === 'covered' ? <span className="label label-default">Already scanned</span> : null}
                <span className="text-muted small">
                  {' '}
                  {range.source}
                  {range.agents > 0 ? ' · ' + range.agents + ' agent(s)' : ''} · {range.from} - {range.to}
                </span>
                {range.reasons.length > 0 && range.status !== 'covered' ? (
                  <ul className="small text-warning" style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                    {range.reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                ) : null}
              </td>
            </tr>
          ))}
          {office.schedule ? (
            <tr data-row="schedule">
              <td>{office.schedule.action === 'set' ? box(office.schedule.key) : null}</td>
              <td className="text-muted">Schedule</td>
              <td>
                {office.schedule.action === 'keep' ? (
                  <span>
                    <span className="label label-default">Keep</span> the relay already has a schedule
                  </span>
                ) : (
                  <span>
                    <strong>{office.schedule.label}</strong>
                    <span className="text-muted small"> · now: {office.schedule.current}</span>
                  </span>
                )}
              </td>
            </tr>
          ) : null}
          <tr data-row="snmp">
            <td>{box(office.snmp.key, !canCredentials || !office.snmp.available)}</td>
            <td className="text-muted">Credential</td>
            <td>
              Try SNMP v2c community &apos;{office.snmp.community}&apos; on the ranges of this office
              {!canCredentials ? <span className="text-muted small"> · needs the edit right on Scanning credentials</span> : null}
              {canCredentials && !office.snmp.available ? <span className="text-muted small"> · no range yet</span> : null}
            </td>
          </tr>
          {office.suggestions.map((s) => (
            <tr key={s.kind} data-row="suggestion">
              <td>
                <i className="fa fa-lightbulb text-info"></i>
              </td>
              <td className="text-muted">Suggestion</td>
              <td>
                <a
                  href="/app/settings/softsee/credentials"
                  onClick={(e) => {
                    e.preventDefault()
                    openCredentials()
                  }}
                >
                  {s.text}
                </a>
                <span className="text-muted small">
                  {' '}
                  · {s.why} ({s.count}: {s.examples.join(', ')})
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AssistantBody({
  page,
  picked,
  toggle,
  result,
  openCredentials,
}: {
  page: DiscoveryAssistantPage
  picked: Set<string>
  toggle(key: string): void
  result: AssistantResult | null
  openCredentials(): void
}) {
  const query = useQueryClient()
  const [rules, setRules] = useState(false)
  const [auto, setAuto] = useState(page.auto.enabled)
  useEffect(() => setAuto(page.auto.enabled), [page.auto.enabled])
  return (
    <div id="discoveryAssistant">
      <p className="text-muted">
        A proposal for <strong>{page.company.name}</strong>, office by office. Ticked rows are applied; rows marked
        &quot;to check&quot; and credentials are never ticked for you.{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            setRules((v) => !v)
          }}
        >
          <i className={'fa ' + (rules ? 'fa-caret-down' : 'fa-caret-right')}></i> How this is decided
        </a>
      </p>
      {rules ? (
        <dl className="small" data-testid="assistant-rules">
          {Object.entries(page.rules).map(([k, v]) => (
            <div key={k}>
              <dt style={{ textTransform: 'capitalize' }}>{k}</dt>
              <dd style={{ marginBottom: 6 }}>{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {result ? (
        <div className={'alert ' + (result.status === 'OK' ? 'alert-success' : 'alert-warning')} data-testid="assistant-result">
          <strong>{result.message}</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {result.results.map((r) => (
              <li key={r.key} className={r.ok ? undefined : 'text-danger'}>
                {r.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {page.offices.length === 0 ? <div className="alert alert-info">This company has no office yet.</div> : null}
      {page.offices.map((office) => (
        <OfficeBlock
          key={office.office.id}
          office={office}
          picked={picked}
          toggle={toggle}
          canApply={page.canApply}
          canCredentials={page.canCredentials}
          openCredentials={openCredentials}
        />
      ))}
      {page.auto.available ? (
        <div className="dsa-auto">
          <label htmlFor="assistantAuto">
            <input
              type="checkbox"
              id="assistantAuto"
              checked={auto}
              disabled={!page.auto.canChange}
              onChange={async (e) => {
                const want = e.target.checked
                setAuto(want)
                try {
                  const reply = await setDiscoveryAuto(want)
                  newAlert(reply.status === 'OK' ? (want ? 'Automatic set-up is on.' : 'Automatic set-up is off.') : 'The setting could not be saved.', reply.status === 'OK' ? 'success' : 'danger')
                  if (reply.status !== 'OK') setAuto(!want)
                  await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'discovery-assistant'] })
                } catch {
                  setAuto(!want)
                  newAlert('The setting could not be saved.', 'danger')
                }
              }}
            />
            <strong>Keep discovery set up automatically</strong>
          </label>
          <p className="help-block small">
            Every night, for new offices, new networks of an office and relays silent for more than 3 days, the ticked-by-default
            part of this proposal is applied on its own - never credentials, never SNMP &apos;public&apos;, never a range to check.
            Each change goes in the discovery settings log.
          </p>
        </div>
      ) : null}
    </div>
  )
}

export function DiscoveryAssistant({ open, onClose }: { open: boolean; onClose(): void }) {
  const query = useQueryClient()
  const navigate = useNavigate()
  const page = useQuery({ ...discoveryAssistantQuery(), enabled: open })
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [result, setResult] = useState<AssistantResult | null>(null)
  const [busy, setBusy] = useState(false)
  const offices = page.data?.offices
  const defaults = useMemo(() => defaultKeys(offices ?? []), [offices])
  useEffect(() => {
    setPicked(new Set(defaults))
  }, [defaults])

  const toggle = (key: string) =>
    setPicked((was) => {
      const next = new Set(was)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const apply = async () => {
    setBusy(true)
    try {
      const reply = await applyDiscoveryAssistant([...picked])
      setResult(reply)
      /* newAlert scrie HTML: numai text facut aici, nimic venit de la server. */
      newAlert(`${reply.applied} of ${reply.results.length} change(s) applied.`, reply.status === 'OK' ? 'success' : 'warning')
      await Promise.all([
        query.invalidateQueries({ queryKey: ['settings', 'softsee'] }),
        query.invalidateQueries({ queryKey: ['shell'] }),
      ])
    } catch (error) {
      newAlert(error instanceof ApiError && error.status === 403 ? 'You do not have access to this action.' : 'The changes could not be applied.', 'danger')
    } finally {
      setBusy(false)
    }
  }

  const close = () => {
    setResult(null)
    onClose()
  }
  const openCredentials = () => {
    close()
    /* Din Policies > Discovery Setup, la Policies > Discovery Credentials (`discoveryDoors.ts`). */
    const to = discoveryTabTarget('credentials')
    if (to.startsWith('/settings/softsee/')) void navigate({ to: '/settings/softsee/$tab', params: { tab: 'credentials' } })
    else void navigate({ to })
  }

  const forbidden = page.error instanceof ApiError && page.error.status === 403
  return (
    <Modal
      id="discoveryAssistantModal"
      open={open}
      large
      onClose={close}
      title={'Set up discovery' + (page.data ? ' - ' + page.data.company.name : '')}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={close}>
            Close
          </button>
          {page.data?.canApply ? (
            <button type="button" className="btn btn-primary" id="assistantApply" disabled={busy || picked.size === 0} onClick={() => void apply()}>
              {busy ? 'Applying…' : `Apply ${picked.size} change${picked.size === 1 ? '' : 's'}`}
            </button>
          ) : null}
        </>
      }
    >
      {forbidden ? <div className="alert alert-danger">You do not have access to this action.</div> : null}
      {!forbidden && !page.data ? <Spinner /> : null}
      {page.data ? <AssistantBody page={page.data} picked={picked} toggle={toggle} result={result} openCredentials={openCredentials} /> : null}
    </Modal>
  )
}
